import type { ProductPageContent } from "./types";

/**
 * Sourced from the Argus repository: `README.md` (Core vs. Enterprise, the
 * two-agent Core kit, the eleven-agent engagement kit), `docs/NO_CHEATING.md`
 * (the validation contract) and `docs/GETTING_STARTED.md`.
 *
 * The one claim this page must never make is that the eleven-agent kit is
 * installable software. It is not sold that way today — it is delivered as a
 * Warden engagement, and the README says so explicitly.
 */
export const ARGUS: ProductPageContent = {
  slug: "argus",
  proofClaim:
    "A finding only counts when the agent's behavior measurably changes — and the verdict is never issued by another model.",
  lede:
    "Argus is an autonomous red team for AI systems. It engages MCP servers, agent frameworks and live endpoints with generic attack techniques, then confirms each finding against a baseline transcript using deterministic detectors.",
  availability:
    "Argus Core is MIT-licensed and installable today. The full eleven-agent kit is delivered as a Warden red-team engagement rather than as installable software.",
  ctas: [
    { label: "Request an engagement", href: "/contact?topic=argus" },
    { label: "Read the integrity contract", href: "#evidence" },
  ],
  problem: {
    heading: "Your existing scanners were built for software that waits to be told what to do",
    body:
      "SAST, DAST and network scanners test the infrastructure underneath an agent. None of them test the surface the agent itself introduces — the tools it can call, the content it will trust, and the instructions it will follow. Argus is complementary to those tools, not a replacement for them.",
    points: [
      {
        title: "1 · The surface is the agent, not the port",
        body: "Prompt surfaces, tool schemas, MCP servers and memory are attack surface that no traditional scanner enumerates.",
      },
      {
        title: "2 · Manual red teaming does not scale",
        body: "Every model update, prompt change and new tool re-opens the question. An assessment that takes weeks is stale before it ships.",
      },
      {
        title: "3 · Most 'findings' are theater",
        body: "A model echoing your payload back proves nothing. Without a behavioural delta you are handing a CISO a screenshot, not a vulnerability.",
      },
    ],
  },
  capabilities: {
    heading: "Attack like an adversary, report like an auditor",
    lede:
      "Argus attacks with generic techniques and records what actually happened, so a finding survives contact with the engineer who has to fix it.",
    items: [
      {
        title: "One input, auto-routed",
        body: "Point Argus at an MCP URL, a GitHub repository, an npm or PyPI package, a local script or a framework fixture, and it dispatches to the right target factory.",
      },
      {
        title: "Sandboxed engagement",
        body: "Untrusted target processes run under Docker with capabilities dropped, no network, a read-only filesystem, an unprivileged user and a process cap.",
      },
      {
        title: "Provider failover built in",
        body: "A single gateway dispatches to OpenAI, Anthropic and Gemini with a dead-provider blacklist, so an engagement survives a provider outage mid-run.",
      },
      {
        title: "Deterministic verdicts",
        body: "Mutation may use a model. Scoring never does. Every verdict comes from a regex, shape or counter detector that you can read and re-run.",
      },
      {
        title: "Reproducible evidence bundles",
        body: "Findings ship as signed, reproducible bundles with the exact trigger, source and reason — suitable for a vulnerability disclosure submission.",
      },
      {
        title: "Runs in the pipeline",
        body: "A GitHub Action, a pre-commit hook and a webhook receiver mean the same engagement can gate a merge rather than sit in a quarterly report.",
      },
    ],
  },
  evidence: {
    heading: "The no-cheating contract",
    lede:
      "Every Argus finding is a claim an operator hands to someone's CISO or regulator, so the rules that keep findings honest are published rather than implied.",
    stats: [
      {
        value: "Zero",
        label: "models in the validation path",
        note: "Payload mutation may use an LLM. Scoring never does — every verdict is emitted by a deterministic detector.",
      },
      {
        value: "Baseline vs. post-attack",
        label: "is what defines a finding",
        note: "The observation engine compares transcripts; a measurable, reproducible behavioural delta is required. An echoed payload is not a finding.",
      },
      {
        value: "No",
        label: "target-aware branching or hardcoded canaries",
        note: "Agents attack with generic techniques only. Canary tokens are injected and detected at runtime, so a technique lands because it works.",
      },
      {
        value: "2 of 11",
        label: "agents ship in the MIT core",
        note: "PI-01 (prompt injection) and EP-11 (environment pivoting) are public. The remaining nine run in a Warden engagement.",
      },
    ],
    methodology:
      "These are engineering constraints in the Argus repository, not measured outcomes — the integrity contract is published in full at docs/NO_CHEATING.md and acceptance tests run real agents against real target fixtures rather than constructing findings by hand. Argus reports what a technique achieved against a specific target; it does not publish a portfolio-wide vulnerability rate, because that number would say more about the sample than about your estate.",
    link: { label: "Argus on GitHub", href: "https://github.com/Odingard/Argus", external: true },
  },
  editions: {
    heading: "Core and engagement",
    lede:
      "Run the public core yourself, or have the team that wrote it run the full kit against your deployment.",
    items: [
      {
        name: "Core",
        price: "MIT licensed",
        blurb: "The public CLI. Self-sufficient for a focused engagement you run yourself.",
        bullets: [
          "PI-01 prompt-injection and EP-11 environment-pivoting agents",
          "Universal target dispatch and sandboxed runtime",
          "Deterministic scoring and offline HTML reports",
          "pip install argus-core",
        ],
      },
      {
        name: "Warden engagement",
        blurb:
          "The eleven-agent kit, the swarm correlation layer and a findings report, delivered by Odingard engineers.",
        bullets: [
          "Nine additional offensive agents across the assessment phases",
          "Multi-agent slate execution and cross-agent correlation",
          "Reproducible evidence bundles and remediation review",
          "Scoped to your frameworks and reporting obligations",
        ],
        cta: { label: "Request an engagement", href: "/contact?topic=argus" },
      },
    ],
  },
  services: {
    heading: "Have it run against your estate",
    body:
      "Warden runs the full kit, interprets the findings against your obligations, and re-tests once the fix lands.",
    links: [
      { label: "Agentic Red Teaming", href: "/warden/agentic-red-teaming" },
      { label: "Agent Readiness Assessment", href: "/warden/agent-readiness" },
      { label: "Tool-Boundary Review", href: "/warden/tool-boundary-review" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Repository", label: "Argus Core on GitHub", href: "https://github.com/Odingard/Argus", external: true },
      { kind: "Documentation", label: "Operator getting-started guide", href: "/docs" },
      { kind: "Research", label: "Agentic attack research", href: "/research" },
      { kind: "Solution", label: "Secure Agentic AI", href: "/secure-agentic-ai" },
    ],
  },
  close: {
    heading: "Find it before someone else does",
    body:
      "Install the core and test your own agents this afternoon, or have Warden run the full kit and hand you findings with the evidence attached.",
    ctas: [
      { label: "Request an engagement", href: "/contact?topic=argus" },
      { label: "Get the MIT core", href: "https://github.com/Odingard/Argus", external: true },
    ],
  },
};
